/**
 * KAN-885: период таблицы CRM по дате взятия в работу и исход таблицы в адресе
 * (разбор и правила в lib/crmTablePeriod); KAN-883: выбор одним полем CrmRangePicker.
 * KAN-887: сортировка ?sort= (lib/crmTableSort).
 * KAN-891/892: кнопки быстрого выбора (Продажи, Неразобранные, Нет движения) как
 * переключатели (lib/crmTableShortcuts); ручная смена периода или исхода снимает активную кнопку (KAN-883).
 * KAN-910: быстрый период пишется именем (?period=today|yesterday|7d|all, setPreset), даты
 * календаря - ?from=&to= (setRange); без периода в адресе пишется умолчание ?period=today.
 * Чтение и запись адреса - useUrlState.
 */
import { useCallback, useMemo } from 'react';
import { useUrlDefaults, useUrlState } from '@/hooks/useUrlState';
import {
  CRM_PERIOD_PARAM,
  CRM_TABLE_DEFAULT_PERIOD,
  CRM_TABLE_PERIOD_KEYS,
  parseCrmTableOutcome,
  parseCrmTablePeriod,
  withCrmTableOutcome,
  withCrmTablePeriod,
} from '@/lib/crmTablePeriod';
import type { CrmRange } from '@/lib/crmTable';
import { CRM_SORT_PARAM, parseCrmTableSort, withCrmTableSort } from '@/lib/crmTableSort';
import {
  activeCrmShortcut,
  toggleCrmShortcut,
  withoutCrmShortcut,
  type CrmTableShortcut,
} from '@/lib/crmTableShortcuts';
import type { PeriodPreset } from '@/lib/periodPresets';
import { withUrlPeriod } from '@/lib/urlPeriod';
import type { CrmTableSort } from '@/types/crm';
import type { CrmTableOutcome } from '@/types/crmPipeline';

export interface CrmTablePeriodState {
  /** Период запроса; null - всё время. */
  range: CrmRange | null;
  outcome: CrmTableOutcome;
  /** KAN-887: применённая сортировка (из адреса или по умолчанию для периода). */
  sort: CrmTableSort;
  /** KAN-891/892: активная кнопка быстрого выбора (не больше одной). */
  shortcut: CrmTableShortcut | null;
  /** Даты из календаря: ?from=&to=. */
  setRange: (range: CrmRange) => void;
  /** KAN-910: быстрый период именем: ?period=<имя>. */
  setPreset: (preset: PeriodPreset) => void;
  /** "Всё время": ?period=all. */
  setAllTime: () => void;
  setOutcome: (outcome: CrmTableOutcome) => void;
  setSort: (sort: CrmTableSort) => void;
  /** Клик по кнопке быстрого выбора: активная - вид по умолчанию, иначе её вид. */
  toggleShortcut: (shortcut: CrmTableShortcut) => void;
}

export function useCrmTablePeriod(): CrmTablePeriodState {
  const { params, update } = useUrlState();
  const from = params.get(CRM_PERIOD_PARAM.from);
  const to = params.get(CRM_PERIOD_PARAM.to);
  const named = params.get(CRM_PERIOD_PARAM.period);
  const outcomeParam = params.get(CRM_PERIOD_PARAM.outcome);
  const sortParam = params.get(CRM_SORT_PARAM);

  useUrlDefaults([
    { key: CRM_PERIOD_PARAM.period, value: CRM_TABLE_DEFAULT_PERIOD, unless: [CRM_PERIOD_PARAM.from, CRM_PERIOD_PARAM.to] },
  ]);

  const period = useMemo(
    () =>
      parseCrmTablePeriod(
        new URLSearchParams({
          [CRM_PERIOD_PARAM.from]: from ?? '',
          [CRM_PERIOD_PARAM.to]: to ?? '',
          [CRM_PERIOD_PARAM.period]: named ?? '',
        })
      ),
    [from, to, named]
  );
  const outcome = parseCrmTableOutcome(outcomeParam, period.range);
  const sort = parseCrmTableSort(sortParam, period.range);

  const writeRange = useCallback(
    (range: CrmRange | null) => update((prev) => withCrmTablePeriod(withoutCrmShortcut(prev), range)),
    [update]
  );

  const setPreset = useCallback(
    (preset: PeriodPreset) => update((prev) => withUrlPeriod(withoutCrmShortcut(prev), CRM_TABLE_PERIOD_KEYS, preset)),
    [update]
  );

  const setAllTime = useCallback(() => writeRange(null), [writeRange]);

  const setOutcome = useCallback(
    (next: CrmTableOutcome) => update((prev) => withCrmTableOutcome(withoutCrmShortcut(prev), next, period.range)),
    [period.range, update]
  );

  const setSort = useCallback(
    (next: CrmTableSort) => update((prev) => withCrmTableSort(prev, next, period.range)),
    [period.range, update]
  );

  const toggleShortcut = useCallback(
    (shortcut: CrmTableShortcut) => update((prev) => toggleCrmShortcut(prev, shortcut)),
    [update]
  );

  return {
    range: period.range,
    outcome,
    sort,
    shortcut: activeCrmShortcut(params),
    setRange: writeRange,
    setPreset,
    setAllTime,
    setOutcome,
    setSort,
    toggleShortcut,
  };
}
